import * as React from "react";
import { useForm, Controller } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from "../../../../components/ui/dialog";
import { Label } from "../../../../components/ui/label";
import { Input } from "../../../../components/ui/input";
import { Button } from "../../../../components/ui/button";
import { Switch } from "../../../../components/ui/switch";
import { commodityOptions } from "data";
import { CommoditySelect } from "./CommoditySelect";

const CommoditySchema = z.object({
  commodityKey: z.string().min(1, "Required"),
  commodityPercentage: z.coerce
    .number({ invalid_type_error: "Required" })
    .int("Whole number only")
    .min(1, "Min 1%")
    .max(100, "Max 100%"),
  refrigerated: z.boolean().default(false),
  generalMerchDetail: z.string().optional(),
}).superRefine((val, ctx) => {
  const isGeneral = val.commodityKey === "general_merchandise";
  if (isGeneral && (!val.generalMerchDetail || val.generalMerchDetail.trim() === "")) {
    ctx.addIssue({
      code: "custom",
      path: ["generalMerchDetail"],
      message: "Please provide details for General Merchandise",
    });
  }
});

export function CommodityDialog({ open, onOpenChange, onAdd, defaultValues, carriers: step8Carrier, updateCarriers }) {
  const {
    control,
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isValid, isSubmitting },
    watch
  } = useForm({
    resolver: zodResolver(CommoditySchema),
    mode: "all",
    defaultValues: {
      commodityKey: "",
      commodityPercentage: undefined,
      refrigerated: false,
      generalMerchDetail: "",
      ...defaultValues,
    },
  });

  React.useEffect(() => {
    if (open) {
      reset({
        commodityKey: defaultValues?.commodityKey || "",
        commodityPercentage: defaultValues?.commodityPercentage ?? undefined,
        refrigerated: defaultValues?.refrigerated ?? false,
        generalMerchDetail: defaultValues?.generalMerchDetail ?? "",
      });
    }
  }, [open, defaultValues, reset]);

  const clampPercent = (v) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return "";
    if (n < 1) return 1;
    if (n > 100) return 100;
    return Math.trunc(n);
  };

  const closeAndReset = () => {
    reset();
    onOpenChange(false);
  };

  const isEdit = !!defaultValues;

  const selectedCommodityKey = watch("commodityKey");
  const showGeneralDetail = selectedCommodityKey === "general_merchandise";

  const hasAccelerant = step8Carrier?.includes("Accelerant") || false;
  const onlyAccelerant = step8Carrier?.length === 1 && hasAccelerant;

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) reset();
        onOpenChange(v);
      }}
    >
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Commodity" : "Add Commodity"}</DialogTitle>
        </DialogHeader>

        <form
          onSubmit={handleSubmit((values) => {
            onAdd(values);
            closeAndReset();
          })}
          className="space-y-5"
        >
          {/* Commodity select */}
          <div className="space-y-2">
            <Label>Select your commodity *</Label>
            <CommoditySelect
              control={control}
              errors={errors}
              commodityOptions={hasAccelerant ? commodityOptions : commodityOptions?.filter((co) => co.forAccelerantOnly !== true)}
              updateCarriers={updateCarriers}
              onlyAccelerant={onlyAccelerant}
            />
            {errors.commodityKey && (
              <p id="commodityKey-error" className="text-xs text-red-600">
                {errors.commodityKey.message}
              </p>
            )}
          </div>

          {/* Detail prompt for General Merchandise */}
          {showGeneralDetail && (
            <div className="space-y-2">
              <Label>Please provide, in detail, what the insured hauls</Label>
              <p className="text-xs text-gray-500">
                (General freight/merchandise is not considered detailed)
              </p>
              <Input
                type="text"
                placeholder="Describe what is hauled"
                {...register("generalMerchDetail")}
                className={errors.generalMerchDetail ? "border-red-500 focus-visible:ring-red-500" : ""}
              />
              {errors.generalMerchDetail && (
                <p className="text-xs text-red-600">{errors.generalMerchDetail.message}</p>
              )}
            </div>
          )}

          {/* Percent input */}
          <div className="space-y-2">
            <Label>What percent of your loads are this commodity? *</Label>
            <Input
              type="number"
              inputMode="numeric"
              step={1}
              min={1}
              max={100}
              placeholder="Enter percent (1-100)"
              className={
                errors.commodityPercentage
                  ? "border-red-500 focus-visible:ring-red-500"
                  : ""
              }
              aria-invalid={!!errors.commodityPercentage}
              aria-describedby={
                errors.commodityPercentage ? "commodityPercentage-error" : undefined
              }
              {...register("commodityPercentage")}
              onBlur={(e) => {
                const clamped = clampPercent(e.target.value);
                setValue("commodityPercentage", clamped, { shouldValidate: true });
              }}
            />
            {errors.commodityPercentage && (
              <p id="commodityPercentage-error" className="text-xs text-red-600">
                {errors.commodityPercentage.message}
              </p>
            )}
          </div>

          {/* Refrigerated toggle */}
          <div className="flex items-center gap-3 pt-2">
            <Controller
              control={control}
              name="refrigerated"
              render={({ field }) => (
                <Switch
                  id="refrigerated-toggle"
                  checked={!!field.value}
                  onCheckedChange={(v) => {
                    field.onChange(v);
                  }}
                />
              )}
            />
            <Label htmlFor="refrigerated-toggle" className="cursor-pointer">
              Is this commodity refrigerated?
            </Label>
          </div>

          <DialogFooter className="pt-4">
            <DialogClose asChild>
              <Button type="button" variant="outline" onClick={() => reset()}>
                Cancel
              </Button>
            </DialogClose>
            <Button
              type="submit"
              className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
              disabled={!isValid || isSubmitting}
            >
              {isEdit ? "Save changes" : "Add commodity"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
